import Link from 'next/link';
import styled, { css } from 'styled-components';

import { Caret } from 'shared/icons/Caret';
import { DESKTOP_PADDING, MOBILE_PADDING } from 'homepage/styles/constants';
import { variableFontRegular } from 'styles/theme/text';
import { t } from 'localization/utils/t';

const TP = 'homepage.components.sections.HomepageModuleHeader';

interface IHomepageModuleHeader {
  addPadding?: boolean;
  count: number;
  onClick: () => void;
  linkUrl: string;
  title: string;
  isInViewPort?: boolean;
  customAnimation?: string;
  isFullScreenGrid?: boolean;
}

export const HomepageModuleHeader = ({
  addPadding = false,
  count,
  onClick,
  linkUrl,
  title,
  isInViewPort,
  customAnimation,
  isFullScreenGrid = false,
}: IHomepageModuleHeader) => (
  <HomepageModuleHeader.Container
    $addPadding={addPadding}
    $isInViewPort={isInViewPort}
    $customAnimation={customAnimation}
    $isFullScreenGrid={isFullScreenGrid}
    onClick={onClick}
  >
    <Link href={linkUrl} passHref prefetch={false}>
      <HomepageModuleHeader.DataLink data-qa="homepage_module_title_link">
        <HomepageModuleHeader.Title data-qa="homepage_module_title">
          {title}
        </HomepageModuleHeader.Title>
      </HomepageModuleHeader.DataLink>
    </Link>
    <HomepageModuleHeader.CountAndArrowContainer>
      <Link href={linkUrl} passHref prefetch={false}>
        <HomepageModuleHeader.Count data-qa="homepage_module_item_count">
          {t(`${TP}.itemsCountLabel`, '{numItems, plural, =1 {1 Item} other {# Items}}', {
            numItems: count,
          })}
        </HomepageModuleHeader.Count>
      </Link>
      <HomepageModuleHeader.CaretContainer>
        <Caret color="white" direction="right" size={8} />
      </HomepageModuleHeader.CaretContainer>
    </HomepageModuleHeader.CountAndArrowContainer>
  </HomepageModuleHeader.Container>
);

HomepageModuleHeader.Container = styled.div<{
  $addPadding: boolean;
  $isInViewPort: boolean;
  $customAnimation?: string;
  $isFullScreenGrid?: boolean;
}>`
  align-items: center;
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;
  padding-right: ${MOBILE_PADDING};
  ${({ $customAnimation }) =>
    $customAnimation &&
    css`
      ${$customAnimation}
    `}

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${({ $addPadding }) => $addPadding && `padding-right: ${DESKTOP_PADDING};`}
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin-bottom: ${({ $isFullScreenGrid }) => $isFullScreenGrid && '16px'};
    ${({ $isInViewPort, $isFullScreenGrid }) =>
      !$isInViewPort &&
      $isFullScreenGrid &&
      css`
        position: fixed;
        left: 12px;
      `}
  }
`;

HomepageModuleHeader.DataLink = styled.a`
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

HomepageModuleHeader.Title = styled.h2`
  font-weight: 400;
  margin: 0;
  text-transform: capitalize;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
`;

HomepageModuleHeader.Count = styled.a`
  font-weight: 400;
  text-transform: capitalize;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};

  &:hover {
    text-decoration: underline;
  }
`;

HomepageModuleHeader.CountAndArrowContainer = styled.div`
  display: flex;
  align-items: center;
`;

HomepageModuleHeader.CaretContainer = styled.div`
  margin-left: 4px;
  display: flex;
`;
